import { Check, Filter, FilterX, Tag, X } from 'lucide-react';
import { useState } from 'react';
import { agentLabelRefName, type AgentAssociationRef } from '@reflex/shared';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { cn } from '@reflex/ui/lib/utils';
import { useAgentListFilterActions } from '@/components/agent/agent-list-filter-context';
import { useRemoveAgentLabel } from '@/hooks/agents/useAgentLabels';

interface AgentHeaderLabelsOverflowProps {
  labels: AgentAssociationRef[];
  /** The agent wearing the tags. Without it, removal is not offered. */
  agentId?: string;
}

/**
 * Session header overflow for tags: a Tag icon and count that opens the run's
 * tags as actions.
 *
 * A tag is a lens on the list, so the names are not just a readout — each row
 * narrows the list to that tag, the same contract {@link TaxonomyChip} gives
 * the tag chips on a row. That is why this is a Popover and not the HoverCard
 * it used to be: a hover card dismisses the moment you set off towards the
 * thing you wanted to click, so a list you can act on has to be click-opened
 * and click-persistent.
 *
 * The trigger carries the active-filter colour when the list is narrowed to
 * one of these tags, so the chip that is doing the narrowing is visible without
 * opening it.
 */
export function AgentHeaderLabelsOverflow({ labels, agentId }: AgentHeaderLabelsOverflowProps) {
  const filterActions = useAgentListFilterActions();
  const removeLabel = useRemoveAgentLabel();
  const [open, setOpen] = useState(false);

  if (labels.length === 0) return null;

  const names = labels.map((label) => agentLabelRefName(label));
  const countLabel = labels.length === 1 ? 'tag' : 'tags';
  const activeLabelId = filterActions?.activeLabelId ?? null;
  const hasActiveFilter = labels.some((label) => label.externalId === activeLabelId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label={`${labels.length} ${countLabel}: ${names.join(', ')}`}
        data-testid="agent-header-labels-trigger"
        data-active-filter={hasActiveFilter ? 'true' : undefined}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'flex h-7 shrink-0 items-center gap-1 rounded-md px-1.5 text-muted-foreground',
          'transition-colors hover:bg-secondary hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'data-[state=open]:bg-secondary data-[state=open]:text-foreground',
          hasActiveFilter && 'bg-ring/10 text-foreground ring-1 ring-inset ring-ring/40',
        )}
      >
        <Tag className="size-3.5" aria-hidden="true" />
        <span className="text-2xs font-medium tabular-nums">{labels.length}</span>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-60 p-1.5"
        aria-label={`${labels.length} ${countLabel}`}
        data-testid="agent-header-labels-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <SectionLabel as="p" className="px-2 py-1">
          {countLabel}
        </SectionLabel>
        <ul className="flex flex-col">
          {labels.map((label) => {
            const labelId = label.externalId;
            const name = agentLabelRefName(label);
            const isActiveFilter = labelId === activeLabelId;

            return (
              <li key={`${label.kind}:${labelId}`} className="group/tag flex items-center gap-0.5">
                <button
                  type="button"
                  data-testid={`agent-header-labels-filter-${labelId}`}
                  aria-pressed={filterActions ? isActiveFilter : undefined}
                  disabled={!filterActions}
                  title={
                    filterActions
                      ? isActiveFilter
                        ? `Clear the ${name} filter`
                        : `Filter to ${name}`
                      : undefined
                  }
                  onClick={() => {
                    if (!filterActions) return;
                    filterActions.setLabelFilter(isActiveFilter ? null : labelId);
                    // The action lands on the list behind the popover, so get
                    // out of the way once it is applied.
                    setOpen(false);
                  }}
                  className={cn(
                    'flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                    'text-foreground transition-colors hover:bg-accent disabled:pointer-events-none',
                    isActiveFilter && 'bg-ring/10',
                  )}
                >
                  <Tag
                    className={cn(
                      'size-3.5 shrink-0 text-muted-foreground',
                      isActiveFilter && 'text-foreground',
                    )}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 truncate">{name}</span>
                  {filterActions ? (
                    <>
                      {isActiveFilter ? (
                        <>
                          <Check
                            className="size-3.5 shrink-0 text-foreground group-hover/tag:hidden"
                            aria-hidden="true"
                          />
                          <FilterX
                            className="hidden size-3.5 shrink-0 text-foreground group-hover/tag:block"
                            aria-hidden="true"
                          />
                        </>
                      ) : (
                        <Filter
                          className="invisible size-3.5 shrink-0 text-muted-foreground group-hover/tag:visible"
                          aria-hidden="true"
                        />
                      )}
                    </>
                  ) : null}
                </button>
                {agentId ? (
                  <button
                    type="button"
                    data-testid={`agent-header-labels-remove-${labelId}`}
                    aria-label={`Remove tag ${name}`}
                    title={`Remove tag ${name}`}
                    disabled={removeLabel.isPending}
                    onClick={() => removeLabel.mutate({ labelId, agentIds: [agentId] })}
                    className={cn(
                      'shrink-0 rounded-md p-1.5 text-muted-foreground opacity-0 transition',
                      'hover:bg-accent hover:text-foreground disabled:opacity-50',
                      'group-hover/tag:opacity-100 focus-visible:opacity-100',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    )}
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
